import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  Image as ImageIcon, Video, Film, Play, BadgeCheck, Sparkles, MapPin,
  UserPlus, Check, Megaphone, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Resolve relative /api/files paths to absolute so <img>/<video> load anywhere. */
export const resolveMedia = (url) =>
  url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url;

/* ============================ STATUS COMPOSER ============================ */
export const StatusComposer = ({ user, onOpen }) => (
  <div className="mg-card-elevated p-4" data-testid="status-composer">
    <div className="flex items-center gap-3">
      <Avatar className="w-11 h-11 border border-primary/40 shrink-0">
        <AvatarImage src={user?.avatar} alt={user?.display_name} />
        <AvatarFallback>{user?.display_name?.[0] || 'M'}</AvatarFallback>
      </Avatar>
      <button
        onClick={onOpen}
        className="flex-1 text-left px-4 py-2.5 rounded-full bg-muted/50 text-muted-foreground hover:bg-muted transition-colors truncate"
        data-testid="composer-open-btn"
      >
        What's happening in Midgard, {user?.display_name?.split(' ')[0] || 'traveler'}?
      </button>
    </div>
    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[hsl(var(--mg-rune)/0.2)]">
      <button onClick={onOpen} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted/60 transition-colors" data-testid="composer-photo-btn">
        <ImageIcon className="w-4 h-4 text-emerald-400" /> Photo
      </button>
      <button onClick={onOpen} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted/60 transition-colors" data-testid="composer-video-btn">
        <Video className="w-4 h-4 text-sky-400" /> Video
      </button>
      <button onClick={onOpen} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted/60 transition-colors" data-testid="composer-reel-btn">
        <Film className="w-4 h-4 text-mg-purple" /> Saga
      </button>
    </div>
  </div>
);

/* ============================== REELS STRIP ============================== */
export const ReelsStrip = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/posts/reels?limit=12')
      .then((r) => setReels(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div data-testid="reels-strip">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2">
          <Film className="w-5 h-5 text-mg-purple" /> Sagas
        </h2>
        <Link to="/reels" className="text-sm text-mg-purple hover:underline flex items-center gap-0.5" data-testid="reels-strip-seeall">
          See all <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      {reels.length === 0 ? (
        <div className="mg-card p-6 text-center" data-testid="reels-strip-empty">
          <Film className="w-8 h-8 mx-auto mb-2 text-mg-purple/70" />
          <p className="text-sm text-muted-foreground">No Sagas yet. Share a vertical video to start the first one.</p>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
          {reels.map((reel) => {
            const media = resolveMedia(reel.media_urls?.[0]);
            return (
              <Link
                to="/reels"
                key={reel.id}
                className="relative flex-none w-32 aspect-[9/16] rounded-xl overflow-hidden group mg-card border border-[hsl(var(--mg-rune)/0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                data-testid={`reels-strip-item-${reel.id}`}
              >
                {media && reel.media_type === 'video' ? (
                  <video
                    src={media}
                    poster={reel.poster_url ? resolveMedia(reel.poster_url) : undefined}
                    className="absolute inset-0 w-full h-full object-cover"
                    muted playsInline preload="none"
                  />
                ) : media ? (
                  <img src={media} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-mg-purple/30 to-accent/20 flex items-center justify-center p-2">
                    <span className="text-[11px] text-center text-white/90 line-clamp-4">{reel.content}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <Play className="w-8 h-8 text-white/90 drop-shadow group-hover:scale-110 transition-transform" />
                </span>
                <span className="absolute left-2 right-2 bottom-2 flex items-center gap-1.5 pointer-events-none">
                  <Avatar className="w-5 h-5 border border-white/50">
                    <AvatarImage src={reel.user?.avatar} />
                    <AvatarFallback className="text-[8px]">{reel.user?.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <span className="text-[10px] font-semibold text-white truncate">{reel.user?.display_name}</span>
                  {reel.user?.is_npc ? (
                    <Sparkles className="w-2.5 h-2.5 text-mg-purple shrink-0" />
                  ) : reel.user?.is_verified ? (
                    <BadgeCheck className="w-2.5 h-2.5 text-white shrink-0" />
                  ) : null}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* =========================== FOLLOW SUGGESTIONS =========================== */
const SuggestionRow = ({ u, onFollowed }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const follow = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${u.id}/follow`);
      setDone(true);
      toast.success(`Following ${u.display_name}`);
      window.dispatchEvent(new CustomEvent('midgard:user-followed', { detail: { id: u.id } }));
      onFollowed?.(u.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not follow');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`suggestion-${u.username}`}>
      <Link to={`/profile/${u.username}`} className="shrink-0">
        <Avatar className="w-10 h-10 border border-[hsl(var(--mg-rune)/0.3)]">
          <AvatarImage src={u.avatar} alt={u.display_name} />
          <AvatarFallback>{u.display_name?.[0]}</AvatarFallback>
        </Avatar>
      </Link>
      <Link to={`/profile/${u.username}`} className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="font-semibold text-sm truncate">{u.display_name}</span>
          {u.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0" />}
          {u.is_npc && (
            <span className="mg-npc-badge scale-90 origin-left"><Sparkles className="w-2.5 h-2.5" />Myth</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate">@{u.username}</span>
          {u.realm && (
            <span className="inline-flex items-center gap-0.5 text-mg-purple"><MapPin className="w-2.5 h-2.5" />{u.realm}</span>
          )}
        </div>
      </Link>
      <Button
        size="sm"
        variant={done ? 'secondary' : 'default'}
        onClick={follow}
        disabled={busy || done}
        className="rounded-full h-8 shrink-0"
        data-testid={`follow-btn-${u.username}`}
      >
        {done ? <><Check className="w-3.5 h-3.5 mr-1" />Following</> : <><UserPlus className="w-3.5 h-3.5 mr-1" />Follow</>}
      </Button>
    </div>
  );
};

export const FollowSuggestions = ({ limit = 5, title = 'Find Your Kin' }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/users/suggestions?limit=${limit}`)
      .then((r) => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [limit]);

  // Keep both rendered instances (feed + rail) in sync when one follows someone.
  useEffect(() => {
    const onFollowed = (e) => setUsers((prev) => prev.filter((x) => x.id !== e.detail?.id));
    window.addEventListener('midgard:user-followed', onFollowed);
    return () => window.removeEventListener('midgard:user-followed', onFollowed);
  }, []);

  const handleFollowed = (id) => {
    setTimeout(() => setUsers((prev) => prev.filter((x) => x.id !== id)), 900);
  };

  if (loading) return null;

  return (
    <div className="mg-card-elevated p-4" data-testid="follow-suggestions">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold">{title}</h3>
        <Link to="/explore" className="text-xs text-mg-purple hover:underline">See more</Link>
      </div>
      {users.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">You've found your kin — no new suggestions for now.</p>
      ) : (
        <div className="space-y-3.5">
          {users.map((u) => <SuggestionRow key={u.id} u={u} onFollowed={handleFollowed} />)}
        </div>
      )}
    </div>
  );
};

/* =============================== AD SLOT ================================= */
export const AdSlotCard = () => (
  <div className="mg-card-elevated overflow-hidden" data-testid="ad-slot-card" aria-label="Merchant's Board">
    <div className="flex items-center justify-between px-4 pt-3">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
        <Megaphone className="w-3 h-3" /> Merchant's Board
      </span>
    </div>
    <div className="p-4 pt-2">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-mg-purple to-accent flex items-center justify-center shrink-0 mg-glow">
          <Sparkles className="w-7 h-7 text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-heading font-bold">Advertise on Midgard</p>
          <p className="text-sm text-muted-foreground line-clamp-2">
            Reach adventurers across the Nine Realms. Your story, seen by the whole settlement.
          </p>
        </div>
      </div>
      <Link to="/ads" className="mg-btn-primary w-full mt-4 text-sm" data-testid="ad-slot-cta">
        Learn more
      </Link>
    </div>
  </div>
);
